import React, { useContext, useMemo, useState } from "react";

import { Column, usePagination, useSortBy, useTable } from 'react-table';
import { InstanceContext } from "../../context/InstanceContext";
import { Link } from "react-router-dom";

import TableCSVExport from '../../component/TableCSVExport';

// Components.
import ReactTable from '../../component/ReactTable';
import StatementToolTip from "../../component/instance/table/StatementToolTip";

// Types
import InstanceType from "../../component/instance/InstanceType";

// Utils
import { getDefaultPageSize, getToolTipPosition } from "../../helpers/utils";
import SearchInput from '../../component/SearchInput';

function ResultsTable(props: { loading: boolean, list: any }) {
    const [tableFilter, setTableFilter] = useState<string>('');
    const {instances} = useContext(InstanceContext);
    const [visibleToolTip, setVisibleToolTip] = useState({
        id: '',
        position: 0
    });

    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const data = useMemo(() => {
        const tableData: any[] = []
        props.list.forEach((item: any) => {
            const getInstance = instances.find(instance => instance.id === item.id)
            if (getInstance) {
                tableData.push({
                    id: getInstance.id,
                    instance: getInstance.name,
                    type: getInstance.type,
                    sqlhash: item.sqlhash,
                    grouphash: item.grouphash,
                    similarity: item.similarity,
                    similarityPercent: `${Math.round(item.similarity * 100)}%`,
                    sqltext: item.sqltext,
                })
            }
        })
        return tableData.filter((row: any) => row.sqltext.toLowerCase().includes(tableFilter.toLowerCase()) || row.instance.toLowerCase().includes(tableFilter.toLowerCase()))
    }, [props.list, tableFilter]);

    const columns: Column[] = useMemo(() => {
        return ([
            {
                accessor: 'id',
                disableSortBy: true
            },
            {
                accessor: 'grouphash',
            },
            {
                accessor: 'sqlhash',
                disableSortBy: true
            },
            {
                Header: 'Instance',
                accessor: 'instance',
                sortType: 'string',
                className: 'border-spacer fw-bold col-width-20 text-nowrap',
            },
            {
                Header: '',
                accessor: 'type',
                className: 'text-center col-width-15',
                headerClassName: 'text-center col-width-15',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic',
                Cell: ({ cell }) => (
                    <React.Fragment>
                        {/* @ts-ignore*/}
                        <InstanceType instance={instances.find(instance => instance.id === cell.row.values.id)} />
                    </React.Fragment>
                )
            },
            {
                Header: 'Statement',
                accessor: 'sqltext',
                className: 'concatenate col-width-60',
                disableSortBy: true,

                Cell: ({cell}: any) => cell.row.values.sqltext ? <Link
                        to={`/instances/${cell.row.values.id}/statement-activity/${cell.row.values.sqlhash}/text`}>{cell.row.values.sqltext.substring(0, 200)}</Link> :
                    <React.Fragment>-</React.Fragment>

            },
            {
                Header: 'Similarity',
                accessor: 'similarity',
                sortType: 'basic',
                className: 'fw-bold col-width-10 text-nowrap text-end',
                Cell: ({ cell }) => {
                    const roundedValue = cell.row.values.similarity > 1 ? 1 : cell.row.values.similarity
                    return `${Math.round(roundedValue * 100)}%`
                }
            },
        ])
    }, [data]);

    const tableInstance = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns: ['id', 'sqlhash', 'grouphash'],
                pageIndex: 0,
                pageSize: getDefaultPageSize()
            },
            autoResetPage: false,
            autoResetSortBy: false,
            autoResetFilters: false,
            autoResetSelectedRows: false,
            autoResetExpanded: false,
            autoResetGroupBy: false,
        },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize}
    } = tableInstance;

    return (
        <div>
            <div className="row row-cols-1 row-cols-md-2 table-search">
                <div className="col col-md-10">
                    <TableCSVExport
                        tableInstance={tableInstance}
                        filename="SQL Search Results"
                    />
                </div>
                <div className="col col-md-2">
                    <SearchInput
                        value={tableFilter}
                        onChange={(e) => setTableFilter(e.target.value)}
                        onClear={clearTableFilter}
                        autoFocus
                    />
                </div>
            </div>
            <ReactTable
                getTableProps={getTableProps}
                getTableBodyProps={getTableBodyProps}
                headerGroups={headerGroups}
                prepareRow={prepareRow}
                page={page}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                pageCount={pageCount}
                gotoPage={gotoPage}
                nextPage={nextPage}
                previousPage={previousPage}
                setPageSize={setPageSize}
                pageIndex={pageIndex}
                pageSize={pageSize}
                dataLength={data.length}
                error={null}
                loading={props.loading}
                renderCell={(cell) => {
                    if (cell.column.id === "sqltext") {
                        const tooltipId = `${cell.row.values.sqlhash}-${cell.row.values.id}`;
                        return (
                            <div className='tooltip-scroll ellipsis'
                                onMouseEnter={(event) => setVisibleToolTip({
                                    id: tooltipId,
                                    position: getToolTipPosition(event)
                                })}
                                onMouseLeave={() => setVisibleToolTip({ id: '', position: 0 })}
                            >
                                {cell.render('Cell')}
                                {tooltipId === visibleToolTip.id && (
                                    <StatementToolTip instanceType={cell.row.values.type}
                                        position={visibleToolTip.position}
                                        id={tooltipId}
                                        link={`/instances/${cell.row.values.id}/statement-activity/${cell.row.values.sqlhash}/text`}
                                        cell={cell} />
                                )}
                            </div>
                        );
                    }
                    return cell.render('Cell');
                }}
            />
        </div>
    );
}

ResultsTable.propTypes = {};

export default ResultsTable;
